import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import {
  DocumentTextIcon,
  DocumentArrowDownIcon,
  CalendarDaysIcon,
  ArrowLeftIcon,
  EyeIcon,
  CheckCircleIcon,
  ClockIcon,
  FolderIcon
} from '@heroicons/react/24/outline';

const TaxDocuments = () => {
  const navigate = useNavigate();
  const [selectedYear, setSelectedYear] = useState('2024-25');
  const [downloadingDoc, setDownloadingDoc] = useState(null);

  // Mock tax documents data
  const taxDocuments = {
    '2024-25': [
      {
        id: 1,
        name: 'Form 16',
        type: 'TDS Certificate',
        description: 'Annual Tax Deduction at Source Certificate',
        issueDate: '2025-07-15',
        status: 'Available',
        fileSize: '2.3 MB',
        taxYear: '2024-25',
        category: 'annual'
      },
      {
        id: 2,
        name: 'Form 16A',
        type: 'TDS Certificate',
        description: 'TDS Certificate for Other Income',
        issueDate: '2025-07-10',
        status: 'Available',
        fileSize: '1.1 MB',
        taxYear: '2024-25',
        category: 'annual'
      },
      {
        id: 3,
        name: 'Salary Certificate',
        type: 'Income Certificate',
        description: 'Annual Salary Certificate for Loan/Visa',
        issueDate: '2025-06-30',
        status: 'Available',
        fileSize: '890 KB',
        taxYear: '2024-25',
        category: 'certificate'
      },
      {
        id: 4,
        name: 'TDS Statement - Q4',
        type: 'Quarterly TDS',
        description: 'Tax Deduction Statement for Q4 (Jan-Mar 2025)',
        issueDate: '2025-04-15',
        status: 'Available',
        fileSize: '1.2 MB',
        taxYear: '2024-25',
        category: 'quarterly'
      },
      {
        id: 5,
        name: 'Investment Declaration',
        type: 'Tax Saving',
        description: 'Submitted 80C Investment Declaration',
        issueDate: '2024-04-30',
        status: 'Submitted',
        fileSize: '1.5 MB',
        taxYear: '2024-25',
        category: 'declaration'
      }
    ],
    '2023-24': [
      {
        id: 6,
        name: 'Form 16',
        type: 'TDS Certificate',
        description: 'Annual Tax Deduction at Source Certificate',
        issueDate: '2024-07-15',
        status: 'Available',
        fileSize: '2.1 MB',
        taxYear: '2023-24',
        category: 'annual'
      },
      {
        id: 7,
        name: 'Form 16A',
        type: 'TDS Certificate',
        description: 'TDS Certificate for Other Income',
        issueDate: '2024-07-10',
        status: 'Available',
        fileSize: '1.0 MB',
        taxYear: '2023-24',
        category: 'annual'
      }
    ]
  };

  const taxSummary = {
    '2024-25': {
      totalIncome: 1100000,
      taxableIncome: 850000,
      totalTDS: 52000,
      refundDue: 0,
      additionalTax: 8500
    },
    '2023-24': {
      totalIncome: 950000,
      taxableIncome: 720000,
      totalTDS: 42000,
      refundDue: 5000,
      additionalTax: 0
    }
  };

  const availableYears = ['2024-25', '2023-24', '2022-23', '2021-22'];

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0
    }).format(amount);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Available': return 'text-green-600 bg-green-100';
      case 'Submitted': return 'text-blue-600 bg-blue-100';
      case 'Processing': return 'text-yellow-600 bg-yellow-100';
      case 'Pending': return 'text-orange-600 bg-orange-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'Available': return CheckCircleIcon;
      case 'Processing': return ClockIcon;
      default: return CheckCircleIcon;
    }
  };

  const getCategoryColor = (category) => {
    switch (category) {
      case 'annual': return 'bg-blue-100 text-blue-800';
      case 'quarterly': return 'bg-green-100 text-green-800';
      case 'certificate': return 'bg-purple-100 text-purple-800';
      case 'declaration': return 'bg-orange-100 text-orange-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const handleDownload = (docId, docName) => {
    setDownloadingDoc(docId);
    // Simulate download
    setTimeout(() => {
      setDownloadingDoc(null);
      alert(`${docName} downloaded successfully!`);
    }, 2000);
  };

  const handleView = (docName) => {
    // In real app, this would open the document in a modal or new tab
    alert(`Opening ${docName} for preview`);
  };

  const currentDocuments = taxDocuments[selectedYear] || [];
  const currentSummary = taxSummary[selectedYear] || {};

  return (
    <div className="min-h-screen bg-apollo-gray">
      <Header />
      
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        {/* Header Section */}
        <div className="mb-6 sm:mb-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
            <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-4 space-y-4 sm:space-y-0">
              <button
                onClick={() => navigate('/payroll')}
                className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-apollo-primary"
              >
                <ArrowLeftIcon className="h-4 w-4 mr-2" />
                Back to Payroll
              </button>
              <div>
                <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Tax Documents</h1>
                <p className="mt-2 text-base sm:text-lg text-gray-600">Download your tax certificates and declarations</p>
              </div>
            </div>
            
            <div className="w-full sm:w-auto">
              <select
                value={selectedYear}
                onChange={(e) => setSelectedYear(e.target.value)}
                className="block w-full sm:w-auto pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-apollo-primary focus:border-apollo-primary rounded-md"
              >
                {availableYears.map(year => (
                  <option key={year} value={year}>FY {year}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Tax Summary */}
        <div className="mb-6 sm:mb-8">
          <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-4 sm:p-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4 sm:mb-6 space-y-2 sm:space-y-0">
              <h3 className="text-lg sm:text-xl font-bold text-gray-900">Tax Summary - FY {selectedYear}</h3>
              <CalendarDaysIcon className="h-5 w-5 sm:h-6 sm:w-6 text-apollo-primary" />
            </div>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 sm:gap-6">
              <div className="text-center sm:text-left lg:text-center">
                <div className="text-xl sm:text-2xl font-bold text-gray-900">{formatCurrency(currentSummary.totalIncome)}</div>
                <div className="text-sm text-gray-500">Total Income</div>
              </div>
              <div className="text-center sm:text-left lg:text-center">
                <div className="text-xl sm:text-2xl font-bold text-apollo-primary">{formatCurrency(currentSummary.taxableIncome)}</div>
                <div className="text-sm text-gray-500">Taxable Income</div>
              </div>
              <div className="text-center sm:text-left lg:text-center">
                <div className="text-xl sm:text-2xl font-bold text-green-600">{formatCurrency(currentSummary.totalTDS)}</div>
                <div className="text-sm text-gray-500">Total TDS</div>
              </div>
              <div className="text-center sm:text-left lg:text-center">
                <div className="text-xl sm:text-2xl font-bold text-blue-600">{formatCurrency(currentSummary.refundDue)}</div>
                <div className="text-sm text-gray-500">Refund Due</div>
              </div>
              <div className="text-center sm:text-left lg:text-center">
                <div className="text-xl sm:text-2xl font-bold text-red-600">{formatCurrency(currentSummary.additionalTax)}</div>
                <div className="text-sm text-gray-500">Additional Tax</div>
              </div>
            </div>
          </div>
        </div>

        {/* Documents Section */}
        <div className="mb-6 sm:mb-8">
          <div className="bg-white rounded-2xl shadow-lg border border-gray-200">
            <div className="px-4 sm:px-6 py-4 border-b border-gray-200">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-2 sm:space-y-0">
                <h3 className="text-lg sm:text-xl font-bold text-gray-900">Available Documents</h3>
                <span className="text-sm text-gray-600">{currentDocuments.length} documents available</span>
              </div>
            </div>
            
            <div className="divide-y divide-gray-200">
              {currentDocuments.length > 0 ? (
                currentDocuments.map((doc) => {
                  const StatusIcon = getStatusIcon(doc.status);
                  
                  return (
                    <div key={doc.id} className="p-4 sm:p-6 hover:bg-gray-50 transition-colors duration-200">
                      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
                        <div className="flex flex-col sm:flex-row sm:items-start space-y-4 sm:space-y-0 sm:space-x-4 flex-1">
                          <div className="p-3 bg-apollo-primary/10 rounded-lg self-start">
                            <DocumentTextIcon className="h-5 w-5 sm:h-6 sm:w-6 text-apollo-primary" />
                          </div>
                          
                          <div className="flex-1">
                            <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-3 mb-2 space-y-2 sm:space-y-0">
                              <h4 className="text-base sm:text-lg font-semibold text-gray-900">{doc.name}</h4>
                              <div className="flex flex-wrap gap-2">
                                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getCategoryColor(doc.category)}`}>
                                  {doc.type}
                                </span>
                                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(doc.status)}`}>
                                  <StatusIcon className="w-3 h-3 mr-1" />
                                  {doc.status}
                                </span>
                              </div>
                            </div>
                            
                            <p className="text-gray-600 mb-3 text-sm sm:text-base">{doc.description}</p>
                            
                            <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-6 text-sm text-gray-500 space-y-2 sm:space-y-0">
                              <div className="flex items-center">
                                <CalendarDaysIcon className="h-4 w-4 mr-1" />
                                Issue Date: {doc.issueDate}
                              </div>
                              <div className="flex items-center">
                                <FolderIcon className="h-4 w-4 mr-1" />
                                Size: {doc.fileSize}
                              </div>
                            </div>
                          </div>
                        </div>
                        
                        <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:space-x-3">
                          <button
                            onClick={() => handleView(doc.name)}
                            className="inline-flex items-center justify-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-apollo-primary"
                          >
                            <EyeIcon className="h-4 w-4 mr-2" />
                            Preview
                          </button>
                          
                          <button
                            onClick={() => handleDownload(doc.id, doc.name)}
                            disabled={downloadingDoc === doc.id}
                            className="inline-flex items-center justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-apollo-primary hover:bg-apollo-secondary focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-apollo-primary disabled:opacity-50"
                          >
                            <DocumentArrowDownIcon className="h-4 w-4 mr-2" />
                            {downloadingDoc === doc.id ? 'Downloading...' : 'Download'}
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })
              ) : (
                <div className="p-8 sm:p-12 text-center">
                  <DocumentTextIcon className="h-10 w-10 sm:h-12 sm:w-12 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-base sm:text-lg font-medium text-gray-900 mb-2">No documents available</h3>
                  <p className="text-gray-600 text-sm sm:text-base">Tax documents for FY {selectedYear} are not yet available.</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Important Notes */}
        <div className="bg-blue-50 border border-blue-200 rounded-2xl p-4 sm:p-6">
          <h4 className="text-base sm:text-lg font-semibold text-blue-900 mb-4">Important Information</h4>
          <div className="space-y-3 text-sm text-blue-800">
            <div className="flex items-start">
              <CheckCircleIcon className="h-4 w-4 sm:h-5 sm:w-5 text-blue-600 mr-2 mt-0.5 flex-shrink-0" />
              <p>Form 16 is typically available by July 31st of the following financial year.</p>
            </div>
            <div className="flex items-start">
              <CheckCircleIcon className="h-4 w-4 sm:h-5 sm:w-5 text-blue-600 mr-2 mt-0.5 flex-shrink-0" />
              <p>All tax documents are digitally signed and legally valid for income tax filing.</p>
            </div>
            <div className="flex items-start">
              <CheckCircleIcon className="h-4 w-4 sm:h-5 sm:w-5 text-blue-600 mr-2 mt-0.5 flex-shrink-0" />
              <p>For any discrepancies in tax documents, please contact the HR department immediately.</p>
            </div>
            <div className="flex items-start">
              <CheckCircleIcon className="h-4 w-4 sm:h-5 sm:w-5 text-blue-600 mr-2 mt-0.5 flex-shrink-0" />
              <p>Keep these documents safe as they are required for income tax return filing.</p>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default TaxDocuments;
